import React, { useState, useEffect } from 'react';
import { getWeather } from '../services/weatherService';
import ScrollHint from './ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';

const WEATHER_EMOJI = {
  clear: '\u2600\uFE0F',
  mostly_clear: '\uD83C\uDF24\uFE0F',
  partly_cloudy: '\u26C5',
  cloudy: '\u2601\uFE0F',
  fog: '\uD83C\uDF2B\uFE0F',
  drizzle: '\uD83C\uDF26\uFE0F',
  rain: '\uD83C\uDF27\uFE0F',
  freezing: '\uD83E\uDDCA',
  snow: '\u2744\uFE0F',
  storm: '\u26C8\uFE0F',
  unknown: '\uD83C\uDF21\uFE0F',
};

const formatDayName = (dateStr, index) => {
  if (index === 0) return 'Today';
  if (index === 1) return 'Tomorrow';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short' });
};

const formatShortDate = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const precipStyle = (chance) => {
  if (chance >= 70) return 'text-blue-700 font-bold';
  if (chance >= 40) return 'text-blue-400';
  return 'text-gray-400';
};

const WeatherBar = () => {
  const { currentLocation } = useLocationContext();
  const [forecast, setForecast] = useState([]);

  useEffect(() => {
    if (!currentLocation?.id) {
      setForecast([]);
      return;
    }
    let cancelled = false;
    const fetchWeather = async () => {
      try {
        const data = await getWeather();
        if (!cancelled) setForecast(data.forecast || []);
      } catch {
        if (!cancelled) setForecast([]);
      }
    };
    fetchWeather();
    return () => { cancelled = true; };
  }, [currentLocation?.id]);

  if (forecast.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <ScrollHint>
        <div className="flex min-w-max">
          {forecast.map((day, i) => (
            <div
              key={day.date}
              className={`flex-1 min-w-[100px] px-3 py-3 text-center border-r last:border-r-0 border-gray-100 ${
                i === 0 ? 'bg-blue-50' : ''
              }`}
            >
              <div className="text-xs font-semibold text-gray-700">
                {formatDayName(day.date, i)}
              </div>
              <div className="text-xs text-gray-400">{formatShortDate(day.date)}</div>
              <div className="text-2xl my-1">
                {WEATHER_EMOJI[day.condition_type] || WEATHER_EMOJI.unknown}
              </div>
              <div className="text-xs text-gray-600 leading-tight">{day.condition}</div>
              <div className="text-sm font-medium text-gray-900 mt-1">
                {day.high != null ? Math.round(day.high) : '--'}° / {day.low != null ? Math.round(day.low) : '--'}°
              </div>
              <div className={`text-xs mt-0.5 ${precipStyle(day.precip_chance || 0)}`}>
                {day.precip_chance || 0}% precip
              </div>
            </div>
          ))}
        </div>
      </ScrollHint>
      <div className="px-3 py-1.5 text-[10px] text-gray-400 text-right border-t border-gray-100">
        Forecast:{' '}
        <a
          href="https://www.weather.gov"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-600 underline"
        >
          National Weather Service
        </a>
      </div>
    </div>
  );
};

export default WeatherBar;
